import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  BarChart3,
  Target,
  Activity,
  RefreshCw,
  Eye,
  Trash2,
  Sparkles,
  Calendar,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MetricCard, KPIGrid } from '@/components/common/MetricCard';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PageHeader, DataTable, Column, TableFilters } from '@/components/common';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import {
  forecastingApi,
  DemandForecast,
  ForecastDashboard,
  ForecastMethod,
  ForecastStatus,
  GenerateForecastDto,
} from '@/api/services/forecasting.api';
import { inventoryApi, InventoryItem } from '@/api/services/inventory.api';
import { locationsApi, Location } from '@/api/services/locations.api';

function getStatusBadgeVariant(status: ForecastStatus): 'default' | 'secondary' | 'outline' {
  switch (status) {
    case 'ACTIVE':
      return 'default';
    case 'DRAFT':
      return 'secondary';
    case 'ARCHIVED':
      return 'outline';
    default:
      return 'secondary';
  }
}

function getMethodLabel(method: ForecastMethod): string {
  switch (method) {
    case 'MOVING_AVERAGE':
      return 'Moving Average';
    case 'EXPONENTIAL_SMOOTHING':
      return 'Exp. Smoothing';
    case 'LINEAR_REGRESSION':
      return 'Linear Regression';
    case 'SEASONAL':
      return 'Seasonal';
    case 'MACHINE_LEARNING':
      return 'Machine Learning';
    default:
      return method;
  }
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function Forecasting() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<ForecastDashboard | null>(null);
  const [forecasts, setForecasts] = useState<DemandForecast[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [methodFilter, setMethodFilter] = useState<string>('all');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Modal states
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  // Form state
  const [generateForm, setGenerateForm] = useState<GenerateForecastDto>({
    itemId: '',
    forecastMethod: 'MOVING_AVERAGE',
    forecastPeriod: 'MONTHLY',
    periodsToForecast: 12,
    lookbackPeriods: 12,
    smoothingFactor: 0.3,
  });

  // Items and locations for selects
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [_loadingItems, setLoadingItems] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await forecastingApi.getDashboard();
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  }, []);
  const fetchItems = useCallback(async () => {
    try {
      setLoadingItems(true);
      const response = await inventoryApi.getAll({ limit: 500 });
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch items:', error);
    } finally {
      setLoadingItems(false);
    }
  }, []);
  const fetchLocations = useCallback(async () => {
    try {
      const response = await locationsApi.getAll({ limit: 100 });
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  }, []);
  const fetchForecasts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await forecastingApi.getAll({
        search: searchQuery || undefined,
        status: statusFilter !== 'all' ? (statusFilter as ForecastStatus) : undefined,
        forecastMethod: methodFilter !== 'all' ? (methodFilter as ForecastMethod) : undefined,
        page: pagination.page,
        limit: pagination.limit,
      });
      setForecasts(response.data.data);
      setPagination((prev) => ({
        ...prev,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages,
      }));
    } catch (error) {
      console.error('Failed to fetch forecasts:', error);
      toast.error('Failed to load forecasts');
    } finally {
      setLoading(false);
    }
  }, [methodFilter, pagination.limit, pagination.page, searchQuery, statusFilter]);

  useEffect(() => {
    fetchDashboard();
    fetchForecasts();
    fetchItems();
    fetchLocations();
  }, [fetchDashboard, fetchForecasts, fetchItems, fetchLocations]);

  useEffect(() => {
    fetchForecasts();
  }, [fetchForecasts]);

  const handleGenerateForecast = async () => {
    if (!generateForm.itemId) {
      toast.error('Please select an inventory item');
      return;
    }

    try {
      const response = await forecastingApi.generate(generateForm);
      toast.success('Forecast generated successfully');
      setShowGenerateModal(false);
      setGenerateForm({
        itemId: '',
        forecastMethod: 'MOVING_AVERAGE',
        forecastPeriod: 'MONTHLY',
        periodsToForecast: 12,
        lookbackPeriods: 12,
        smoothingFactor: 0.3,
      });
      fetchForecasts();
      fetchDashboard();
      // Open what was just generated. This used to pop the detail sheet; the
      // page is a better landing spot for something you will want to cite.
      navigate(`/forecasting/${response.data.id}`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to generate forecast');
    }
  };

  const handleActivateForecast = async (forecast: DemandForecast) => {
    try {
      await forecastingApi.update(forecast.id, { status: 'ACTIVE' });
      toast.success('Forecast activated');
      fetchForecasts();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to activate forecast');
    }
  };

  const handleArchiveForecast = async (forecast: DemandForecast) => {
    try {
      await forecastingApi.update(forecast.id, { status: 'ARCHIVED' });
      toast.success('Forecast archived');
      fetchForecasts();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to archive forecast');
    }
  };

  const handleDeleteForecast = async (forecast: DemandForecast) => {
    if (!confirm('Are you sure you want to delete this forecast?')) return;

    try {
      await forecastingApi.delete(forecast.id);
      toast.success('Forecast deleted');
      fetchForecasts();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to delete forecast');
    }
  };

  const handleSyncHistorical = async () => {
    if (!generateForm.itemId) {
      toast.error('Please select an item first');
      return;
    }

    try {
      const response = await forecastingApi.syncHistorical({
        itemId: generateForm.itemId,
        locationId: generateForm.locationId,
      });
      toast.success(`Synced ${response.data.recordsSynced} historical records`);
    } catch (error) {
      toast.error('Failed to sync historical data');
    }
  };

  const handleCalculateAccuracy = async () => {
    try {
      const response = await forecastingApi.calculateAccuracy({ monthsBack: 6 });
      toast.success(`Calculated accuracy for ${response.data.periodsCalculated} periods`);
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to calculate accuracy');
    }
  };

  const forecastColumns: Column<DemandForecast>[] = [
    {
      key: 'item',
      linkTo: (forecast) => `/forecasting/${forecast.id}`,
      header: 'Item',
      sortable: true,
      accessorFn: (forecast) => forecast.item.name,
      render: (forecast) => (
        <div>
          <p className="font-medium">{forecast.item.name}</p>
          <p className="text-xs text-slate-500">{forecast.item.sku}</p>
        </div>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      sortable: true,
      accessorFn: (forecast) => forecast.location?.name ?? 'All Locations',
      render: (forecast) => <>{forecast.location?.name || 'All Locations'}</>,
    },
    {
      key: 'forecastMethod',
      header: 'Method',
      sortable: true,
      render: (forecast) => (
        <Badge variant="outline">{getMethodLabel(forecast.forecastMethod)}</Badge>
      ),
    },
    {
      key: 'forecastPeriod',
      header: 'Period',
      sortable: true,
    },
    {
      key: 'startDate',
      header: 'Date Range',
      render: (forecast) => (
        <span className="text-sm">
          {formatDate(forecast.startDate)} - {formatDate(forecast.endDate)}
        </span>
      ),
    },
    {
      key: 'confidence',
      header: 'Confidence',
      sortable: true,
      render: (forecast) =>
        forecast.confidence !== null ? (
          <span
            className={
              forecast.confidence >= 70
                ? 'text-emerald-600'
                : forecast.confidence >= 50
                  ? 'text-amber-600'
                  : 'text-red-600'
            }
          >
            {forecast.confidence}%
          </span>
        ) : (
          <>-</>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (forecast) => (
        <Badge variant={getStatusBadgeVariant(forecast.status)}>{forecast.status}</Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right',
      render: (forecast) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/forecasting/${forecast.id}`);
            }}
          >
            <Eye className="h-4 w-4" />
          </Button>
          {forecast.status === 'DRAFT' && (
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                handleActivateForecast(forecast);
              }}
            >
              <Activity className="h-4 w-4 text-emerald-500" />
            </Button>
          )}
          {forecast.status === 'ACTIVE' && (
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                handleArchiveForecast(forecast);
              }}
            >
              <Calendar className="h-4 w-4 text-slate-500" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteForecast(forecast);
            }}
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Demand Forecasting"
          description="Projected demand by item and location, with the method and confidence behind each figure."
          actions={
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCalculateAccuracy}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Calculate Accuracy
              </Button>
              <Button onClick={() => setShowGenerateModal(true)}>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Forecast
              </Button>
            </div>
          }
        />
      </motion.div>

      {/* KPI Cards */}
      <motion.div variants={fadeUp}>
        <KPIGrid columns={4}>
          <MetricCard
            label="Total Forecasts"
            value={dashboard?.kpis.totalForecasts || 0}
            icon={<BarChart3 className="h-5 w-5" />}
            sentiment="info"
          />
          <MetricCard
            label="Active Forecasts"
            value={dashboard?.kpis.activeForecasts || 0}
            icon={<Activity className="h-5 w-5" />}
            sentiment="success"
          />
          <MetricCard
            label="Avg Accuracy (MAPE)"
            value={
              dashboard?.kpis.averageMape !== null && dashboard?.kpis.averageMape !== undefined
                ? `${dashboard.kpis.averageMape}%`
                : 'N/A'
            }
            icon={<Target className="h-5 w-5" />}
            sentiment="info"
          />
          <MetricCard
            label="Accuracy Trend"
            value={
              dashboard?.kpis.accuracyTrend !== null && dashboard?.kpis.accuracyTrend !== undefined
                ? `${dashboard.kpis.accuracyTrend > 0 ? '+' : ''}${dashboard.kpis.accuracyTrend}%`
                : 'N/A'
            }
            icon={<TrendingUp className="h-5 w-5" />}
            sentiment={
              dashboard?.kpis.accuracyTrend !== null && dashboard?.kpis.accuracyTrend !== undefined
                ? dashboard.kpis.accuracyTrend > 0
                  ? 'success'
                  : 'danger'
                : 'warning'
            }
            trend={
              dashboard?.kpis.accuracyTrend !== null && dashboard?.kpis.accuracyTrend !== undefined
                ? {
                    direction:
                      dashboard.kpis.accuracyTrend > 0 ? ('up' as const) : ('down' as const),
                    value: `${Math.abs(dashboard.kpis.accuracyTrend)}%`,
                    text: 'vs previous period',
                  }
                : undefined
            }
          />
        </KPIGrid>

        {/* Forecasts Table */}
        <DataTable<DemandForecast>
          toolbar={
            <TableFilters
              search={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder="Search forecasts..."
              filters={[
                {
                  id: 'status',
                  label: 'Statuses',
                  value: statusFilter,
                  options: [
                    { value: 'DRAFT', label: 'Draft' },
                    { value: 'ACTIVE', label: 'Active' },
                    { value: 'ARCHIVED', label: 'Archived' },
                  ],
                  onChange: setStatusFilter,
                  placeholder: 'Status',
                },
                {
                  id: 'method',
                  label: 'Methods',
                  value: methodFilter,
                  options: [
                    { value: 'MOVING_AVERAGE', label: 'Moving Average' },
                    { value: 'EXPONENTIAL_SMOOTHING', label: 'Exp. Smoothing' },
                    { value: 'LINEAR_REGRESSION', label: 'Linear Regression' },
                  ],
                  onChange: setMethodFilter,
                  placeholder: 'Method',
                  width: 'w-[180px]',
                },
              ]}
            />
          }
          onRowClick={(forecast) => navigate(`/forecasting/${forecast.id}`)}
          data={forecasts}
          columns={forecastColumns}
          loading={loading}
          emptyTitle="No forecasts found"
          emptyDescription="Generate your first forecast to get started."
          pagination
          exportable
          columnToggle
        />

        {/* Generate Forecast Modal */}
        <Dialog open={showGenerateModal} onOpenChange={setShowGenerateModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Generate Demand Forecast</DialogTitle>
              <DialogDescription>
                Configure the forecast parameters and generate predictions based on historical
                consumption data.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Inventory Item *</Label>
                <Select
                  value={generateForm.itemId}
                  onValueChange={(value) => setGenerateForm({ ...generateForm, itemId: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an item" />
                  </SelectTrigger>
                  <SelectContent>
                    {items.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name} ({item.sku})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Location (Optional)</Label>
                <Select
                  value={generateForm.locationId || 'all'}
                  onValueChange={(value) =>
                    setGenerateForm({
                      ...generateForm,
                      locationId: value === 'all' ? undefined : value,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name} ({loc.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Forecast Method</Label>
                  <Select
                    value={generateForm.forecastMethod}
                    onValueChange={(value) =>
                      setGenerateForm({ ...generateForm, forecastMethod: value as ForecastMethod })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MOVING_AVERAGE">Moving Average</SelectItem>
                      <SelectItem value="EXPONENTIAL_SMOOTHING">Exp. Smoothing</SelectItem>
                      <SelectItem value="LINEAR_REGRESSION">Linear Regression</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Forecast Period</Label>
                  <Select
                    value={generateForm.forecastPeriod}
                    onValueChange={(value) =>
                      setGenerateForm({ ...generateForm, forecastPeriod: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DAILY">Daily</SelectItem>
                      <SelectItem value="WEEKLY">Weekly</SelectItem>
                      <SelectItem value="MONTHLY">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Periods to Forecast</Label>
                  <Input
                    type="number"
                    min={1}
                    max={52}
                    value={generateForm.periodsToForecast}
                    onChange={(e) =>
                      setGenerateForm({
                        ...generateForm,
                        periodsToForecast: parseInt(e.target.value) || 12,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>Lookback Periods</Label>
                  <Input
                    type="number"
                    min={3}
                    max={52}
                    value={generateForm.lookbackPeriods}
                    onChange={(e) =>
                      setGenerateForm({
                        ...generateForm,
                        lookbackPeriods: parseInt(e.target.value) || 12,
                      })
                    }
                  />
                </div>
              </div>

              {generateForm.forecastMethod === 'EXPONENTIAL_SMOOTHING' && (
                <div className="space-y-2">
                  <Label>Smoothing Factor (α)</Label>
                  <Input
                    type="number"
                    min={0.01}
                    max={1}
                    step={0.05}
                    value={generateForm.smoothingFactor}
                    onChange={(e) =>
                      setGenerateForm({
                        ...generateForm,
                        smoothingFactor: parseFloat(e.target.value) || 0.3,
                      })
                    }
                  />
                  <p className="text-xs text-slate-500">
                    Higher values give more weight to recent observations (0.1-0.3 recommended)
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  placeholder="Add any notes about this forecast..."
                  value={generateForm.notes || ''}
                  onChange={(e) => setGenerateForm({ ...generateForm, notes: e.target.value })}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSyncHistorical}
                  disabled={!generateForm.itemId}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sync Historical Data
                </Button>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGenerateModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleGenerateForecast}>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Forecast
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Forecast Detail Modal */}
        {/* A record is inspected here, not acted on, so this is a sheet
            rather than a modal. A dialog blocks the page behind it, which is
            right for a decision and wrong for reading. */}
        {/* The detail sheet lived here. It is a page now, at
            /forecasting/:id, so a forecast can be cited and returned to. */}
      </motion.div>
    </motion.main>
  );
}
